import L from 'leaflet'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface WeatherLabelOptions extends L.LayerOptions {
  size?: number
  direction?: number
  stroke?: boolean
  color?: string
  weight?: number
  text?: string
  textOptions?: {
    repeat?: boolean
    fillColor?: string
    attributes?: Record<string, string>
    below?: boolean
    dataAnchor?: string
    offset?: number
    center?: boolean
  }
}

class WeatherLabel extends L.Path {
  private _latlng: L.LatLng
  private _size: number
  private _direction: number
  private _windSymbol: number[][]
  private _text!: string
  private _textOptions: WeatherLabelOptions['textOptions']
  private _textNode: SVGTextElement | undefined

  constructor(
    latlng: L.LatLngLiteral | L.LatLng,
    options?: WeatherLabelOptions
  ) {
    super(options)

    if (latlng === undefined) {
      throw new Error('Please give a valid lat/lon-position')
    }

    L.setOptions(this, options || {})

    // this._leaflet_id = this._id
    this._latlng = L.latLng(latlng)
    this._size = options?.size || 16
    this._direction = ((options?.direction || 0) * Math.PI) / 180
    this._windSymbol = [
      [0.5, 0, -0.5, 0],
      [-0.3, 0.2, -0.5, 0, -0.3, -0.2],
    ]
    this._windSymbol.push(
      [0.5, 0, 0.5, -0.2 * 2],
      [0.25, 0, 0.25, -0.2 * 2],
      [0, 0, 0, -0.2 * 1]
    )

    this.setStroke(options?.stroke || true)
    this.setColor(options?.color || '#ff0000')
    this.setWeight(options?.weight || 1)
  }

  setColor(color: string): this {
    this.setStyle({ color })
    return this.redraw()
  }

  setFillOpacity(fillOpacity: number): this {
    this.setStyle({ fillOpacity })
    return this.redraw()
  }

  setOpacity(opacity: number): this {
    this.setStyle({ opacity })
    return this.redraw()
  }

  setWeight(weight: number): this {
    this.setStyle({ weight })
    return this.redraw()
  }

  setFill(fill: boolean): this {
    this.setStyle({ fill })
    return this.redraw()
  }

  setStroke(stroke: boolean): this {
    this.setStyle({ stroke })
    return this.redraw()
  }

  setFillColor(fillColor: string): this {
    this.setStyle({ fillColor })
    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _project(): void {}

  protected _update(): void {
    this._setPath()
  }

  protected _setPath(): void {
    const _ = this as any
    _._path.setAttribute('d', this.getPathString())
  }

  setLatLng(latlng: L.LatLngLiteral | L.LatLng): this {
    const oldLatLng = this._latlng
    this._latlng = L.latLng(latlng)
    this.fire('move', { oldLatLng, latlng })
    return this.redraw()
  }

  setDirection(direction: number): this {
    this._direction = direction
    return this.redraw()
  }

  private _textRedraw() {
    const text = this._text
    const textOptions = this._textOptions
    if (text) {
      this.setText('', {}).setText(text, textOptions)
    }
  }

  override onAdd(map: MyMap): this {
    this._textRedraw()
    return super.onAdd(map)
  }

  setText(text: string, options?: WeatherLabelOptions['textOptions']): this {
    this._text = text
    this._textOptions = options || {}

    if (!L.Browser.svg || this._map === undefined) {
      return this
    }

    options = L.Util.extend(
      {
        repeat: false,
        fillColor: 'black',
        attributes: {},
        below: false,
        'data-anchor': null,
        offset: undefined,
        center: false,
      },
      options
    )

    if (!text) {
      if (this._textNode && this._textNode.parentNode) {
        ;(this._map as any)._renderer.removeChild(this._textNode)
        this._textNode = undefined
      }
      return this
    }

    text = text.replace(/ /g, '\u00A0')

    const pathDefId = `pathdef-${L.Util.stamp(this)}`
    const rendererContainer = (this._map as any)._renderer
      ._container as SVGSVGElement
    const svgText = L.SVG.create('text') as SVGTextElement
    const textPath = L.SVG.create('textPath') as SVGTextPathElement
    const strokeWidth =
      options.offset ||
      Number.parseFloat((this as any)._path.getAttribute('stroke-width'))

    textPath.setAttributeNS(
      'http://www.w3.org/1999/xlink',
      'xlink:href',
      `#${pathDefId}`
    )
    svgText.setAttribute('dy', strokeWidth.toString())

    for (const attrName in options.attributes) {
      svgText.setAttribute(attrName, options.attributes[attrName])
    }

    if (options as any['data-anchor']) {
      svgText.dataset.anchor = options as any['data-anchor']
    }

    textPath.appendChild(document.createTextNode(text))
    svgText.appendChild(textPath)
    this._textNode = svgText

    if (options.below) {
      rendererContainer.insertBefore(svgText, rendererContainer.firstChild)
    } else {
      rendererContainer.appendChild(svgText)
    }

    if (options.center) {
      const boxWidth = svgText.getBBox().width
      svgText.setAttribute('dx', (16 - boxWidth / 2).toString())
    }

    return this
  }

  // private _getLatSize(): number {
  //   return this._getLatSizeOf(this._size)
  // }

  // private _getLngSize(): number {
  //   return this._getLngSizeOf(this._size)
  // }

  // private _getLatSizeOf(size: number): number {
  //   return (size / 40075017) * 360
  // }

  // private _getLngSizeOf(size: number): number {
  //   return ((size / 40075017) * 360) / Math.cos((Math.PI / 180) * this._latlng.lat)
  // }

  getLatLng(): L.LatLng {
    return this._latlng
  }

  private _rotate(point: number[], angle: number): [number, number] {
    const [x, y] = point
    const sin = Math.sin(angle)
    const cos = Math.cos(angle)
    return [x * cos - y * sin, x * sin + y * cos]
  }

  private _rotateAllPoints(points: number[][], angle: number): number[][] {
    return points.map((p) =>
      this._rotate(
        p.map((v) => v * this._size),
        angle
      )
    )
  }

  private _transformAllPointsToView(points: number[]): number[] {
    const layerPoint = this._map.latLngToLayerPoint(this._latlng)
    return points.map((value, index) => {
      if (index % 2 === 0) {
        return layerPoint.x + value
      } else {
        return layerPoint.y - points[index - 1]
      }
    })
  }

  private _createPathFromPoints(points: number[]): string {
    let pathString = ''
    for (let i = 0; i < points.length; i += 2) {
      const x = points[i]
      const y = points[i + 1]
      if (!pathString) {
        pathString = `M ${x} ${y} `
      } else {
        pathString += `L ${x} ${y} `
      }
    }
    return `${pathString} Z`
  }

  private _getViewAngleFromModel(modelAngle: number): number {
    return Math.PI / 2 - modelAngle
  }

  // private _resizeAndMovePoint(
  //   point: [number, number],
  //   scale: [number, number],
  //   offset: [number, number]
  // ): [number, number] {
  //   return [point[0] * scale[0] + offset[0], point[1] * scale[1] + offset[1]]
  // }

  private _createWindPathString(): string {
    let pathString = ''
    const viewAngle = this._getViewAngleFromModel(this._direction)
    for (let i = 0; i < this._windSymbol.length; ++i) {
      const transformedPoints = this._transformAllPointsToView(
        this._rotateAllPoints(this._windSymbol[i] as any, viewAngle) as any
      )
      if (i === 0) {
        pathString = this._createPathFromPoints(transformedPoints)
      } else {
        pathString += ` ${this._createPathFromPoints(transformedPoints)}`
      }
    }
    return pathString
  }

  getPathString(): string {
    return this._createWindPathString()
  }

  static create(
    latlng: L.LatLngLiteral | L.LatLng,
    options?: WeatherLabelOptions
  ): WeatherLabel {
    return new WeatherLabel(latlng, options)
  }
}

const weatherLabel = (
  latlng: L.LatLngLiteral | L.LatLng,
  options?: WeatherLabelOptions
) => WeatherLabel.create(latlng, options)

export { WeatherLabel, weatherLabel }
